import { useCallback, memo } from "react";
import { Fade, Stack } from "@mui/material";
import { ProductFilter } from "../../filters/ProductFilter/ProductFilter";
import { PRODUCT_LIST_UPLOAD_PROPS } from "../../filters/ProductFilter/constants";
import { ProductFilterValue } from "../../filters/ProductFilter/types";
import { EXPORT_INCLUSION_TYPES } from "../types";

// Memoized ProductFilter wrapper to prevent unnecessary re-renders
const MemoizedProductFilter = memo(
  ({
    selectedProductIds,
    onProductsSelect,
  }: {
    selectedProductIds: number[];
    onProductsSelect: (value: ProductFilterValue) => void;
  }) => (
    <Fade in={true} timeout={300}>
      <Stack sx={{ mx: 4 }}>
        <ProductFilter
          selectedProductIds={selectedProductIds}
          onProductsSelect={onProductsSelect}
          uploadProps={PRODUCT_LIST_UPLOAD_PROPS}
          autoCompleteProps={{ disablePortal: false }}
        />
      </Stack>
    </Fade>
  )
);

MemoizedProductFilter.displayName = "MemoizedProductFilter";

export const useProductFilterRenderer = (
  inclusion: EXPORT_INCLUSION_TYPES,
  selectedProductIds: number[],
  onProductsSelect: (ids: number[]) => void
) => {
  const handleProductsSelect = useCallback(
    (value: ProductFilterValue) => {
      onProductsSelect(value.ids);
    },
    [onProductsSelect]
  );

  return useCallback(
    (optionValue: string) => {
      // Only render if the current option is SELECTED and it's actually selected
      if (
        optionValue === EXPORT_INCLUSION_TYPES.SELECTED &&
        inclusion === EXPORT_INCLUSION_TYPES.SELECTED
      ) {
        return (
          <MemoizedProductFilter
            selectedProductIds={selectedProductIds}
            onProductsSelect={handleProductsSelect}
          />
        );
      }
      return null;
    },
    [inclusion, selectedProductIds, handleProductsSelect]
  );
};
